import { type ReactElement, useEffect, useState } from 'react';

type Props = {
  /**
   * the default Composer component to show.
   */
  defaultComposerComponent: ReactElement;

  /**
   * The temporary dynamic content that replaces the default composer.
   */
  dynamicComposerContent: ReactElement | null;

  /**
   * This triggers the transition when set to TRUE
   */
  showDynamicComposerContent?: boolean;

  /**
   * Callback for after dynamic content is shown, use this if you need to
   * set programmatic focus on the dynamic content,
   */
  onDynamicComposerContentShown?: () => void;

  /**
   * Callback for after the default content is shown, use this if you need to
   * set programmatic focus back on the default content
   */
  onDefaultComposerContentShown?: () => void;
};

/**
 * This hook manages setting state between a default component and a temporary
 * dynamic content to show in its place. It relies on a transition to be defined in the parent,
 * the result of which will trigger this state update.
 *
 * See `Conversation.tsx` for example usage.
 */
export const useDynamicContent = (props: Props) => {
  const {
    showDynamicComposerContent = false,
    defaultComposerComponent,
    dynamicComposerContent,
    onDynamicComposerContentShown,
    onDefaultComposerContentShown,
  } = props;
  const [composerComponent, setComposerComponent] = useState<ReactElement | null>(
    showDynamicComposerContent ? dynamicComposerContent : defaultComposerComponent
  );
  const [composerHidden, setComposerHidden] = useState<boolean>(false);
  const [showingDynamic, setShowingDynamic] = useState<boolean>(showDynamicComposerContent);

  useEffect(() => {
    if (!composerHidden && showDynamicComposerContent !== showingDynamic) {
      const animationFrame = requestAnimationFrame(() => {
        // start transition to collapse the default composer
        setComposerHidden(true);
      });

      return () => {
        cancelAnimationFrame(animationFrame);
      };
    }

    return () => undefined;
  }, [composerHidden, showDynamicComposerContent, defaultComposerComponent, showingDynamic]);

  // The defaultComposerComponent can update when a child component prop updates
  // This useEffect ensures the the prop updates are preserved within defaultComposerComponent
  useEffect(() => {
    if (!showingDynamic) {
      setComposerComponent(defaultComposerComponent);
    }
  }, [showingDynamic, defaultComposerComponent]);

  const handleTransitionEnd = () => {
    // at this point, the collapse transition is ended
    // so we can update the component to be shown
    setComposerComponent(showDynamicComposerContent ? dynamicComposerContent : defaultComposerComponent);
    // and unset the collapse transition so it expands fully into view
    setComposerHidden(false);
    setShowingDynamic(showDynamicComposerContent);

    if (showDynamicComposerContent) {
      onDynamicComposerContentShown?.();
    } else {
      onDefaultComposerContentShown?.();
    }
  };

  return {
    composerComponent,
    composerHidden,
    showingDynamic,
    onTransitionEnd: handleTransitionEnd,
  };
};
